import api from './api';
import { ApiResponse, Question, QuestionRequest } from '../types';

export const questionService = {
  getQuestions: async (questionnaireId: number): Promise<Question[]> => {
    const response = await api.get<ApiResponse<Question[]>>(`/admin/questionnaires/${questionnaireId}/questions`);
    return response.data.data;
  },

  getQuestion: async (questionnaireId: number, questionId: number): Promise<Question> => {
    const response = await api.get<ApiResponse<Question>>(`/admin/questionnaires/${questionnaireId}/questions/${questionId}`);
    return response.data.data;
  },

  createQuestion: async (questionnaireId: number, question: QuestionRequest): Promise<Question> => {
    const response = await api.post<ApiResponse<Question>>(`/admin/questionnaires/${questionnaireId}/questions`, question);
    return response.data.data;
  },

  updateQuestion: async (questionnaireId: number, questionId: number, question: QuestionRequest): Promise<Question> => {
    const response = await api.put<ApiResponse<Question>>(`/admin/questionnaires/${questionnaireId}/questions/${questionId}`, question);
    return response.data.data;
  },

  deleteQuestion: async (questionnaireId: number, questionId: number): Promise<void> => {
    await api.delete(`/admin/questionnaires/${questionnaireId}/questions/${questionId}`);
  },

  reorderQuestions: async (questionnaireId: number, questionIds: number[]): Promise<void> => {
    await api.post(`/admin/questionnaires/${questionnaireId}/questions/reorder`, { questionIds });
  },

  copyQuestion: async (questionnaireId: number, questionId: number): Promise<Question> => {
    const response = await api.post<ApiResponse<Question>>(`/admin/questionnaires/${questionnaireId}/questions/${questionId}/copy`);
    return response.data.data;
  },

  setPageBreak: async (questionnaireId: number, questionId: number, pageBreakBefore: boolean): Promise<void> => {
    await api.post(`/admin/questionnaires/${questionnaireId}/questions/${questionId}/page-break`, { pageBreakBefore });
  },

  /**
   * 只更新评分（option.score / matrix_cell_scores.score），不动其他字段。
   * 已发布问卷也允许调。后端每改一条会写 score_change_logs 审计记录。
   */
  updateScores: async (
    questionnaireId: number,
    questionId: number,
    payload: {
      optionScores?: Record<number, number>;
      matrixCellScores?: { rowLabel: string; columnOptionId: number; score: number }[];
    },
  ): Promise<Question> => {
    const response = await api.patch<ApiResponse<Question>>(
      `/admin/questionnaires/${questionnaireId}/questions/${questionId}/scores`,
      payload,
    );
    return response.data.data;
  },

  /** 评分变更审计日志（最新 500 条） */
  getScoreChangeLogs: async (questionnaireId: number): Promise<ScoreChangeLogEntry[]> => {
    const response = await api.get<ApiResponse<ScoreChangeLogEntry[]>>(
      `/admin/questionnaires/${questionnaireId}/questions/scores/audit-log`,
    );
    return response.data.data;
  },
};

export interface ScoreChangeLogEntry {
  id: number;
  questionnaireId: number;
  questionId: number;
  targetType: 'OPTION' | 'MATRIX_CELL';
  targetOptionId: number | null;
  matrixRowLabel: string | null;
  oldScore: number | null;
  newScore: number;
  changedBy: string;
  changedAt: string;
}
